import {
  Box,
  ComponentMultiStyleConfig,
  Link,
  List,
  ListItem,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../../lib/util/common-util";

export type CMPaginationProps = {
  /**
   * ページの総数。内部の計算用
   */
  pageCount: number;

  /**
   * 現在のページ番号として使用
   */
  currentPageNumber: number;

  /**
   * 表示するページ数
   */
  marginPagesDisplayed?: number;

  /**
   * 前のページに遷移する際のラベル
   */
  labelPrevious?: string;

  /**
   * 後のページに遷移する際のラベル
   */
  labelNext?: string;

  onPageChange?: (selectedPageNumber: number) => void;
} & ComponentStyleProps;

/**
 * 検索結果数とページ番号コンポーネント.
 */
export const CMPagination = ({
  pageCount,
  currentPageNumber,
  marginPagesDisplayed,
  labelPrevious,
  labelNext,
  onPageChange,

  sx,
  ...rest
}: CMPaginationProps) => {
  const styles = useMultiStyleConfig("CMPaginationStyles", {});

  /**
   * 総ページ数を配列に変換
   * pageCountが30の場合、[1,2,3,...,29,30]の配列を作成
   */
  const totalPages: number[] = useMemo<number[]>(() => {
    return [...Array(pageCount).keys()].map((page) => {
      return page + 1;
    });
  }, [pageCount]);

  /**
   * 表示するページ番号たちを切り替えるための判定に使用
   */
  const rangeCount: { minNumber: number; maxNumber: number } = useMemo(() => {
    if (isNullish(marginPagesDisplayed)) {
      // rangeCountはmarginPagesDisplayedがあるときに使用されるので0の値が使用されることはない
      return {
        minNumber: 0,
        maxNumber: 0,
      };
    }
    // marginPagesDisplayedが存在する場合のみ後続の処理が行われる

    if (marginPagesDisplayed % 2 === 0) {
      // 表示するページ数が割りきれる数の場合、
      return {
        // 最小値。現在のページがこの値以下なら1から表示するページ数分の番号を表示するようにする
        minNumber: marginPagesDisplayed / 2 + 1,
        // 最大値。現在のページがこの値以上なら最後のページがページの総数の番号になるように表示するページ数分の番号を表示
        maxNumber: pageCount - marginPagesDisplayed / 2,
      };
    } else {
      // 表示するページ数が割りきれない数の場合、
      return {
        minNumber: (marginPagesDisplayed + 1) / 2,
        maxNumber: pageCount - (marginPagesDisplayed - 1) / 2,
      };
    }
  }, [marginPagesDisplayed, pageCount]);

  /**
   * 表示されるページ番号たち
   * @param pageCount ページの総数
   * @param marginPagesDisplayed 表示するページ数
   * @param currentPageNumber 現在のページ番号
   */
  const _pages: number[] = useMemo(() => {
    if (isNullish(marginPagesDisplayed) || marginPagesDisplayed >= pageCount) {
      // marginPagesDisplayedが存在しない。または、総ページ数以上の場合、全ページ番号表示
      return totalPages;
    }
    // marginPagesDisplayedが存在するかつ総ページ数より小さい場合,後続処理が行われる

    if (hasValue(rangeCount) && currentPageNumber <= rangeCount.minNumber) {
      // 現在のページがページ番号を切り替えるための最小値以下の場合

      return [...Array(marginPagesDisplayed).keys()].map((page) => {
        // 1から表示するページ数分の番号を返却
        return page + 1;
      });
    } else if (
      hasValue(rangeCount) &&
      currentPageNumber >= rangeCount.maxNumber
    ) {
      // 現在のページがページ番号を切り替えるための最大値以上の場合

      return [...Array(marginPagesDisplayed).keys()].map((page) => {
        // 最後のページがページの総数の番号になるように表示するページ数分の番号を返却
        return pageCount - marginPagesDisplayed + page + 1;
      });
    } else {
      // 現在のページがページ番号を切り替えるための最小値より大きくて最大値より小さい場合

      return [...Array(marginPagesDisplayed).keys()].map((page) => {
        // 現在のページが中央に表示されるように値を返却
        // 例 12 13 14 現在のページ(15) 16 17 18 のように現在のページが中央に来る並びなる
        return currentPageNumber - rangeCount.minNumber + page + 1;
      });
    }
  }, [
    marginPagesDisplayed,
    pageCount,
    rangeCount,
    currentPageNumber,
    totalPages,
  ]);

  /**
   * 1ページ前へボタンがクリックされたときの処理
   */
  const _handlePrevious = (): void => {
    // 現在のページが最初のページの場合は、処理を行わない
    if (currentPageNumber === 1) {
      return;
    }
    const changedPageNumber = currentPageNumber - 1;
    onPageChange?.(changedPageNumber);
  };

  /**
   * 1ページ次へボタンがクリックされたときの処理
   */
  const _handleNext = (): void => {
    // 現在のページが最後のページの場合は、処理を行わない
    if (currentPageNumber === pageCount) {
      return;
    }
    const changedPageNumber = currentPageNumber + 1;
    onPageChange?.(changedPageNumber);
  };

  /**
   * ページ番号を直接クリックされたときの処理
   * @param changedPageNumber
   */
  const _handleClick = (changedPageNumber: number): void => {
    onPageChange?.(changedPageNumber);
  };

  return (
    <Box {...rest} sx={{ ...styles.container, ...sx }}>
      {pageCount !== 0 && (
        <>
          <List sx={styles.list}>
            <ListItem key={"previous"} sx={styles.listItem}>
              <Link onClick={_handlePrevious} sx={styles.link}>
                {labelPrevious}
              </Link>
            </ListItem>
            {_pages.map((page) => {
              return (
                <ListItem
                  key={page}
                  sx={
                    page === currentPageNumber
                      ? styles.activeItem
                      : styles.listItem
                  }
                >
                  <Link onClick={() => _handleClick(page)} sx={styles.link}>
                    {page}
                  </Link>
                </ListItem>
              );
            })}
            <ListItem key={"next"} sx={styles.listItem}>
              <Link onClick={_handleNext} sx={styles.link}>
                {labelNext}
              </Link>
            </ListItem>
          </List>
        </>
      )}
    </Box>
  );
};

export const CMPaginationStyles: ComponentMultiStyleConfig = {
  parts: ["container", "list", "listItem", "activeItem", "link"],
  baseStyle: {
    container: {},
    list: {
      display: "flex",
      gap: "20px",
      fontSize: "16px",
      lineHeight: "24px",
      color: "blue.600",
    },
    listItem: {},
    activeItem: { color: "gray.900" },
    link: {},
  },
};
